import { ChangeDetectionStrategy, Component, computed, forwardRef, inject } from "@angular/core";

import { HoverRevealContainerDirective } from "../../hover-reveal";

import { BitTableV2Component } from "./table-v2.component";

/**
 * A body row. The component host *is* the row element (CSS grid container).
 * Cannot use `display: contents` here because the `cdk-virtual-scroll`
 * directive applies `transform: translateY(...)` to position rows in the
 * virtual viewport, and transform has no effect on contents-display boxes.
 *
 * Grid layout:
 * - In column-def mode, the row reads `gridTemplateColumns` from the parent
 *   `<bit-table-v2>` so all rows align on the same column tracks. Use `1fr`
 *   or explicit pixel widths on `<bit-column>` for cross-row alignment.
 *   Intrinsic sizing keywords (`max-content`, `min-content`) won't align
 *   across rows because each row computes its tracks independently.
 * - In manual mode (no `<bit-column>`), `grid-template-columns` is unset and
 *   the row falls back to `grid-auto-flow: column; grid-auto-columns: 1fr`
 *   so projected `<bit-cell>` children each get an equal share.
 *
 * In `list`, `<bit-table-v2>` wraps the row in a `<bit-item>` that owns the chrome.
 *
 * The row is a `bitHoverRevealContainer`, so `bitHoverReveal` elements in its cells show on row
 * hover or focus.
 */
@Component({
  selector: "bit-row",
  template: "<ng-content></ng-content>",
  host: {
    role: "row",
    "[class]": "hostClasses()",
    "[style.grid-template-columns]": "gridTemplateColumns()",
    "[style.height.px]": "fixedHeight()",
  },
  hostDirectives: [HoverRevealContainerDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BitRowComponent {
  private readonly table = inject(
    forwardRef(() => BitTableV2Component),
    { optional: true },
  );

  protected readonly gridTemplateColumns = computed(() => this.table?.gridTemplateColumns());

  private readonly isList = computed(() => this.table?.presentation() === "list");

  /** Virtualized rows are positioned by offset, so they must render at exactly the height the scroll strategy assumed. */
  protected readonly fixedHeight = computed(() =>
    this.isList() ? undefined : this.table?.virtualRowHeight(),
  );

  /** Row chrome. Grid in both presentations; the rest is `table`-only. */
  protected readonly hostClasses = computed(() =>
    [
      // `group/row` lets cell templates style on row hover with `group-hover/row:*`. Rows don't
      // nest, so the shared group name scopes to each row's own subtree. To show actions on row
      // hover, prefer `bitHoverReveal`.
      "tw-group/row",
      "tw-grid",
      "tw-grid-flow-col",
      "tw-auto-cols-fr",
      // If the row is fixed height, tall cell overflow should be clipped and the row should not
      // expand its height to fit the tall cell's content
      ...(this.fixedHeight() != null ? ["tw-grid-rows-1", "tw-overflow-clip"] : []),
      ...(this.isList()
        ? ["tw-w-full", "tw-min-w-0"]
        : [
            // Omitted when virtualized: a min-height would clamp a `virtualRowHeight`
            // below it, breaking the offsets the scroll strategy positions rows at.
            ...(this.fixedHeight() != null ? [] : ["tw-min-h-14"]),
            "tw-border-0",
            "tw-border-b",
            "tw-border-solid",
            "tw-border-border-base",
            "hover:tw-bg-bg-brand-softer",
            // Focus carries the hover fill plus a ring. Inset so it isn't clipped by
            // the body's scroll container or by a fixed-height row's `overflow-clip`.
            "has-[:focus-visible]:tw-bg-bg-brand-softer",
            "has-[:focus-visible]:tw-ring-2",
            "has-[:focus-visible]:tw-ring-inset",
            "has-[:focus-visible]:tw-ring-border-focus",
            // Outranks the hover and focus rules above on specificity.
            "has-[[data-selection-input]:checked]:tw-bg-bg-brand-soft",
          ]),
    ].join(" "),
  );
}
